// src/pages/userprofile/ApiKeysPage.tsx
// API Keys management inside user Settings — list, create, revoke, delete.

import { useState, useEffect, useCallback } from 'react';
import { observer } from '@react';
import { useApikey, useI18n } from '@react/hooks';
import { apikeyApi } from '@core/api';
import {
  Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, ErrorAlert,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '@react/components';
import { PermissionMatrix } from '../apikey/PermissionMatrix';
import type { APIKey } from '@core/types/apikey';
import type { TranslationKey, TranslationParams } from '@react/hooks';
import {
  getStatusDisplay, validateCreateForm, formatKeyPrefix, formatDate,
  formatExpiration, canRevoke, canDelete,
  EMPTY_CREATE_FORM,
} from '../../features/apikeys/logic';
import type { CreateKeyFormState } from '../../features/apikeys/logic';

type TFn = (key: TranslationKey, params?: TranslationParams) => string;

// ─── Inline SVG icons (no emoji, no external deps) ─────────────

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (
    <svg
      className={`${className ?? ''} transition-transform ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ─── Copy to clipboard helper ───────────────────────────────────

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text in a temporary input
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);
  return { copied, copy };
}

// ─── Created Key Banner ─────────────────────────────────────────

function CreatedKeyBanner({ fullKey, onDismiss, t }: { fullKey: string; onDismiss: () => void; t: TFn }) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Alert variant="success" dismissible onDismiss={onDismiss}>
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('apikeys.keyCreatedTitle')}</p>
        <p className="text-xs text-muted-foreground">{t('apikeys.keyCreatedWarning')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm break-all select-all">
            {fullKey}
          </code>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => copy(fullKey)}
            aria-label={t('common.copy')}
          >
            {copied
              ? <CheckIcon className="h-4 w-4 text-green-600" />
              : <CopyIcon className="h-4 w-4" />
            }
          </Button>
        </div>
      </div>
    </Alert>
  );
}

// ─── Create Key Form ────────────────────────────────────────────

function CreateKeyForm({ onCreated, onCancel, t }: {
  onCreated: (fullKey: string) => void;
  onCancel: () => void;
  t: TFn;
}) {
  const [form, setForm] = useState<CreateKeyFormState>({ ...EMPTY_CREATE_FORM });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [showPermissions, setShowPermissions] = useState(false);

  const handleChange = (field: keyof CreateKeyFormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateCreateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const req = {
        name: form.name.trim(),
        description: form.description.trim(),
        scopes: [],
        permission_matrix: form.permissions,
        never_expires: form.neverExpires,
      } as any;
      if (!form.neverExpires && form.expiresAt) {
        req.expires_at = new Date(form.expiresAt).toISOString();
      }

      // Call API directly to get the one-time full key from CreateAPIKeyResponse
      const response = await apikeyApi.CreateAPIKey(req);
      onCreated(response.key);
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <CardHeader>
          <CardTitle>{t('apikeys.createTitle')}</CardTitle>
          <CardDescription>{t('apikeys.createDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}

          <Input
            label={t('apikeys.fieldName')}
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={t('apikeys.fieldNamePlaceholder')}
            error={errors.name ? t(errors.name) : undefined}
            required
          />

          <Input
            label={t('apikeys.fieldDescription')}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder={t('apikeys.fieldDescriptionPlaceholder')}
            error={errors.description ? t(errors.description) : undefined}
          />

          {/* Permission matrix (collapsible) */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowPermissions((v) => !v)}
              className="flex items-center gap-2 w-full text-left"
            >
              <ChevronIcon className="h-4 w-4 text-muted-foreground" open={showPermissions} />
              <span className="text-sm font-medium text-foreground">
                {t('apikey.scopes')}
                <span className="text-destructive ml-0.5">*</span>
              </span>
              {!showPermissions && Object.values(form.permissions).some((a) => a.length > 0) && (
                <Badge variant="default">
                  {Object.values(form.permissions).filter((a) => a.length > 0).length}
                </Badge>
              )}
            </button>
            <p className="text-xs text-muted-foreground">{t('apikey.scopesHelp')}</p>
            {showPermissions && (
              <PermissionMatrix
                value={form.permissions}
                onChange={(permissions) => setForm((prev) => ({ ...prev, permissions }))}
              />
            )}
            {errors.permissions && (
              <p className="text-xs text-destructive mt-1">{t(errors.permissions)}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.neverExpires}
                onChange={(e) => handleChange('neverExpires', e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span className="text-sm font-medium text-foreground">{t('apikeys.neverExpires')}</span>
            </label>

            {!form.neverExpires && (
              <Input
                label={t('apikeys.fieldExpiresAt')}
                type="date"
                value={form.expiresAt}
                onChange={(e) => handleChange('expiresAt', e.target.value)}
                error={errors.expiresAt ? t(errors.expiresAt) : undefined}
                required
              />
            )}
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.creating') : t('apikeys.generateKey')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

// ─── Revoke Dialog (inline) ─────────────────────────────────────

function RevokeForm({ keyName, onConfirm, onCancel, isLoading, t }: {
  keyName: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  t: TFn;
}) {
  const [reason, setReason] = useState('');

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="text-destructive">{t('apikeys.revokeTitle')}</CardTitle>
        <CardDescription>{t('apikeys.revokeWarning', { name: keyName })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          label={t('apikeys.revokeReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('apikeys.revokeReasonPlaceholder')}
        />
      </CardContent>
      <CardFooter className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>{t('common.cancel')}</Button>
        <Button variant="danger" onClick={() => onConfirm(reason)} disabled={isLoading}>
          {isLoading ? t('common.loading') : t('apikeys.revokeConfirm')}
        </Button>
      </CardFooter>
    </Card>
  );
}

// ─── Key Row ────────────────────────────────────────────────────

function KeyRow({ apiKey, onRevoke, onDelete, onUpdatePermissions, t }: {
  apiKey: APIKey;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdatePermissions: (id: string, permissions: Record<string, string[]>) => void;
  t: TFn;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editPermissions, setEditPermissions] = useState<Record<string, string[]>>({});
  const [isSaving, setIsSaving] = useState(false);

  const status = getStatusDisplay(apiKey.status);
  const keyId = apiKey.entity?.id ?? '';
  const expirationLabel = formatExpiration(apiKey);
  const expirationText = expirationLabel.startsWith('apikeys.') ? t(expirationLabel) : expirationLabel;

  // Build permissions from the API key's permission_matrix or scopes
  const currentPermissions = buildPermissionsFromKey(apiKey);

  const handleStartEdit = () => {
    setEditPermissions({ ...currentPermissions });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditPermissions({});
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdatePermissions(keyId, editPermissions);
      setEditMode(false);
    } finally {
      setIsSaving(false);
    }
  };

  const permissionCount = Object.values(currentPermissions).filter((a) => a.length > 0).length;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <div className="flex items-center gap-2">
            <ChevronIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" open={expanded} />
            <div>
              <p className="text-sm font-medium text-foreground">{apiKey.name}</p>
              {apiKey.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{apiKey.description}</p>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <code className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-xs">
            {formatKeyPrefix(apiKey.key_prefix)}
          </code>
        </TableCell>
        <TableCell>
          <Badge variant={status.variant}>{t(status.labelKey)}</Badge>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatDate(apiKey.created_at)}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : t('apikeys.neverUsed')}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {expirationText}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {canRevoke(apiKey) && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onRevoke(keyId)}>
                {t('apikeys.revoke')}
              </Button>
            )}
            {canDelete(apiKey) && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(keyId)}>
                {t('common.delete')}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/20 px-6 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{t('apikey.scopes')}</span>
                  {permissionCount > 0 && (
                    <Badge variant="default">{permissionCount}</Badge>
                  )}
                </div>
                {canRevoke(apiKey) && !editMode && (
                  <Button variant="secondary" size="sm" onClick={handleStartEdit}>
                    {t('common.edit')}
                  </Button>
                )}
                {editMode && (
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                      {t('common.cancel')}
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? t('common.saving') : t('common.save')}
                    </Button>
                  </div>
                )}
              </div>

              <PermissionMatrix
                value={editMode ? editPermissions : currentPermissions}
                onChange={editMode ? setEditPermissions : () => {}}
                disabled={!editMode}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** Extract permission matrix from APIKey (permission_matrix or fallback to scopes). */
function buildPermissionsFromKey(apiKey: APIKey): Record<string, string[]> {
  // Try permission_matrix first (new field)
  const matrix = (apiKey as any).permission_matrix;
  if (matrix?.entries) {
    const result: Record<string, string[]> = {};
    for (const entry of matrix.entries) {
      if (entry.section && entry.actions?.length > 0) {
        result[entry.section] = [...entry.actions];
      }
    }
    return result;
  }

  // Fallback: try permissions field as Record<string, string[]>
  const perms = (apiKey as any).permissions;
  if (perms && typeof perms === 'object' && !('items' in perms)) {
    return perms;
  }

  return {};
}

// ─── Main Page ──────────────────────────────────────────────────

export const ApiKeysPage = observer(() => {
  const store = useApikey();
  const { t } = useI18n();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null);

  // Load keys on mount
  useEffect(() => {
    store.ListAPIKeys({
      pagination: { cursor: '', limit: 50, direction: 'forward' },
      sort: [],
      date_range: {} as never,
      filters: {} as never,
    });
  }, []);

  const handleKeyCreated = (fullKey: string) => {
    setCreatedKey(fullKey);
    setShowCreateForm(false);
    // Refresh list to include the new key
    store.ListAPIKeys({
      pagination: { cursor: '', limit: 50, direction: 'forward' },
      sort: [],
      date_range: {} as never,
      filters: {} as never,
    });
  };

  const handleRevoke = async (reason: string) => {
    if (!revokeTargetId) return;
    try {
      await store.RevokeAPIKey(revokeTargetId, { reason });
      setRevokeTargetId(null);
      // Refresh list
      store.ListAPIKeys({
        pagination: { cursor: '', limit: 50, direction: 'forward' },
        sort: [],
        date_range: {} as never,
        filters: {} as never,
      });
    } catch {
      // Error shown via store.error
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('apikeys.deleteConfirm'))) return;
    try {
      await store.DeleteAPIKey(id);
    } catch {
      // Error shown via store.error
    }
  };

  const handleUpdatePermissions = async (keyId: string, permissions: Record<string, string[]>) => {
    await store.UpdateAPIKey(keyId, {
      api_key: { permissions } as any,
      field_mask: ['permissions'],
    });
    // Refresh list
    store.ListAPIKeys({
      pagination: { cursor: '', limit: 50, direction: 'forward' },
      sort: [],
      date_range: {} as never,
      filters: {} as never,
    });
  };

  const revokeTarget = revokeTargetId ? store.items.find((k) => k.entity?.id === revokeTargetId) : null;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('apikeys.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('apikeys.subtitle')}</p>
        </div>
        {!showCreateForm && (
          <Button onClick={() => { setShowCreateForm(true); setCreatedKey(null); }}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('apikeys.createKey')}
          </Button>
        )}
      </div>

      {/* Error */}
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Created key banner — shown once after creation */}
      {createdKey && (
        <CreatedKeyBanner fullKey={createdKey} onDismiss={() => setCreatedKey(null)} t={t} />
      )}

      {/* Create form */}
      {showCreateForm && (
        <CreateKeyForm
          onCreated={handleKeyCreated}
          onCancel={() => setShowCreateForm(false)}
          t={t}
        />
      )}

      {/* Revoke form */}
      {revokeTarget && (
        <RevokeForm
          keyName={revokeTarget.name}
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTargetId(null)}
          isLoading={store.isLoading}
          t={t}
        />
      )}

      {/* Hint about key visibility */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-muted/50 border border-border">
        <EyeOffIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">{t('apikeys.securityHint')}</p>
      </div>

      {/* Keys table */}
      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('apikeys.columnName')}</TableHead>
              <TableHead>{t('apikeys.columnKey')}</TableHead>
              <TableHead>{t('apikeys.columnStatus')}</TableHead>
              <TableHead>{t('apikeys.columnCreated')}</TableHead>
              <TableHead>{t('apikeys.columnLastUsed')}</TableHead>
              <TableHead>{t('apikeys.columnExpires')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="text-muted-foreground">{t('common.loading')}</div>
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={7} message={t('apikeys.emptyState')} />
            ) : (
              store.items.map((key) => (
                <KeyRow
                  key={key.entity?.id}
                  apiKey={key}
                  onRevoke={setRevokeTargetId}
                  onDelete={handleDelete}
                  onUpdatePermissions={handleUpdatePermissions}
                  t={t}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default ApiKeysPage;
